import {
  mapValueProperties,
  getErrors,
  extractErrorMessage
} from '@/utilities/common'
import {
  required,
  maxLength,
  minLength
} from 'vuelidate/lib/validators'
import { mapState } from 'vuex'
import {
  includeNumberRegex,
  addressRegex
} from '@/utilities/common/validators'
import { startsWith, toLower, cloneDeep } from 'lodash'
import Vue from 'vue'
import {
  detectedCulture
} from 'utilities/common/culture'
import {
  counties
} from 'utilities/cluture/counties-list'

export default {
  props: {
    countries: {
      type: Array,
      required: true
    },

    shipping: {
      type: Object,
      required: true
    },

    hasBilling: {
      type: Boolean,
      default: false
    },

    billing: {
      type: Object,
      default () {
        return {}
      }
    }
  },

  data () {
    return {
      showValidations: false,
      editable: false,
      preEditAddress: {},
      routeName: this.$route.name,
      currentCountryCode: detectedCulture.countryCode,
      counties: counties[detectedCulture.countryCode],
      isShow: false,
      topLabel: false,
      promotions:[],
      allianzCodeApplied:false
    }
  },

  computed: {
    ...mapValueProperties('shipping', [
      'countryCode',
      'stateCode',
      'city',
      'zipCode',
      'addition',
      'street',
      'houseNumber',
      'extension',
      'line1',
      'line2',
      'county'
    ]),

    ...mapValueProperties('billing', [
      'countryCode',
      'stateCode',
      'city',
      'zipCode',
      'addition',
      'street',
      'houseNumber',
      'extension',
      'line1',
      'line2',
      'county'
    ], {
      prefixed: true
    }),

    ...mapState('account', {
      ableToUpdateAddress: state => state.profile.personalDetail.ableToUpdateAddress
    }),
    ...mapState('checkout', {
      cartDetails: state => state.cartDetails
    }),

    ...mapState('culture', ['countryProvinces']),

    selectedCountry: {
      get () {
        return this.countries.length === 1 ? this.countries[0] : this.countryCode &&
          this.countries.find(country => country.code === this.countryCode)
      },

      set (selectedCountry) {
        this.countryCode = selectedCountry.code
        this.stateCode = null
      }
    },

    selectedBillingCountry: {
      get () {
        return this.countries.length === 1 ? this.countries[0] : this.billingCountryCode &&
          this.countries.find(country => country.code === this.billingCountryCode)
      },

      set (selectedBillingCountry) {
        this.billingCountryCode = selectedBillingCountry.code
        this.billingStateCode = null
      }
    },

    selectedCountryState: {
      get () {
        return this.selectedCountry.states &&
          this.stateCode &&
          (this.selectedCountry.states.find(state => state.code === this.stateCode) ||
          this.selectedCountry.statesDuplicate.find(state => state.code === this.stateCode))
      },

      set (selectedCountryState) {
        this.stateCode = selectedCountryState.code
      }
    },

    selectedBillingCountryState: {
      get () {
        return this.selectedBillingCountry.states &&
          this.billingStateCode &&
          (this.selectedBillingCountry.states.find(state => state.code === this.billingStateCode) ||
          this.selectedBillingCountry.statesDuplicate.find(state => state.code === this.billingStateCode))
      },

      set (selectedBillingCountry) {
        this.billingStateCode = selectedBillingCountry.code
      }
    },

    selectedCountryProvince: {
      get () {
        return this.selectedCountry.code === 'CA' && this.countryProvinces.provinces &&
          this.stateCode &&
          (this.countryProvinces.provinces.find(province => province.code === this.stateCode) ||
          this.countryProvinces.provincesDuplicate.find(province => province.code === this.stateCode))
      },

      set (selectedCountryProvince) {
        this.stateCode = selectedCountryProvince.code
        this.$store.commit('culture/setCities', this.stateCode)
      }
    },

    selectedCountryCity: {
      get () {
        return this.countryProvinces.cities &&
          this.city &&
          (this.countryProvinces.cities.find(item => item.code === this.city) ||
          this.countryProvinces.citiesDuplicate.find(item => item.code === this.city))
      },

      set (selectedCountryCity) {
        this.city = selectedCountryCity.code
      }
    },

    selectedBillingCountryProvince: {
      get () {
        return this.selectedCountry.code === 'CA' && this.countryProvinces.provinces &&
          this.billingStateCode &&
          (this.countryProvinces.provinces.find(province => province.code === this.billingStateCode) ||
          this.countryProvinces.provincesDuplicate.find(province => province.code === this.billingStateCode))
      },

      set (selectedBillingCountryProvince) {
        this.billingStateCode = selectedBillingCountryProvince.code
        this.$store.commit('culture/setBillingCities', this.billingStateCode)
      }
    },

    selectedBillingCountryCity: {
      get () {
        return this.countryProvinces.billingCities &&
          this.billingCity &&
          (this.countryProvinces.billingCities.find(item => item.code === this.billingCity) ||
          this.countryProvinces.billingCitiesDuplicate.find(item => item.code === this.billingCity))
      },

      set (selectedBillingCountryCity) {
        this.billingCity = selectedBillingCountryCity.code
      }
    },

    hasBillingAddress: {
      get () {
        return this.hasBilling
      },

      set (hasBillingAddress) {
        this.$store.commit('account/setHasBilling', hasBillingAddress)
      }
    },

    address () {
      return ({
        shipping: {
          ...this.shipping,
          countryCode: this.shipping.countryCode || (this.selectedCountry && this.selectedCountry.code) // first address: default countryCode when only one country
        },
        hasBilling: this.hasBillingAddress,
        billing: this.hasBillingAddress ? {
          ...this.billing,
          countryCode: this.billing.countryCode || (this.selectedBillingCountry && this.selectedBillingCountry.code)
        } : {
          type: 'Billing',
          countryCode: null,
          stateCode: null,
          city: null,
          zipCode: null,
          addition: null,
          street: null,
          houseNumber: null,
          extension: null,
          line1: null,
          line2: null,
          county: null
        }
      })
    },

    formatShiping () {
      return ({
        ...this.shipping,
        countryCode: this.shipping.countryCode || (this.selectedCountry && this.selectedCountry.code) // first address: default countryCode when only one country
      })
    },

    formatBilling () {
      if (this.hasBillingAddress) {
        return {
          ...this.billing,
          countryCode: this.billing.countryCode || (this.selectedBillingCountry && this.selectedBillingCountry.code)
        }
      } else {
        return {
          type: 'Billing',
          countryCode: null,
          stateCode: null,
          city: null,
          zipCode: null,
          addition: null,
          street: null,
          houseNumber: null,
          extension: null,
          line1: null,
          line2: null,
          county: null
        }
      }
    },

    normalizedAddress () {
      let address = []
      if (!this.hasBillingAddress) {
        address.push(this.formatShiping)
      } else {
        address.push(this.formatShiping, this.formatBilling)
      }
      return address
    },

    countiesDuplicate () {
      return counties[this.currentCountryCode]
    }
  },

  validations () {
    let shippingValidationObject
    let billingValidationObject
    if (this.selectedCountry.code === 'GB' || this.selectedCountry.code === 'ES' || this.selectedCountry.code === 'BE' || this.selectedCountry.code === 'IT' || this.selectedCountry.code === 'AT' || this.selectedCountry.code === 'FR' || this.selectedCountry.code === 'SE' || this.selectedCountry.code === 'RO' || this.selectedCountry.code === 'CZ') {
      shippingValidationObject = {
        line1: {
          required,
          Addressline1 (value) { 
            if(this.selectedCountry.code === 'ES' || this.selectedCountry.code === 'AT' || this.selectedCountry.code === 'SE' || this.selectedCountry.code === 'BE' || this.selectedCountry.code === 'CZ' || this.selectedCountry.code === 'RO'){
              if (!value) return true
              return /^[\da-zA-Z\a-zA-ZùÙüÜáčďéěíňóřšťúůýžäàåáčďéěíňóřšťúůýžâáëèéïìíöòóôüùúÄÅÀÁÁČĎÉĚÍŇÓŘŠŤÚŮÝŽËÈÉÏÌÍÖÒÓÜÚñÑ,\s]{1,32}$/.test(value) 
            } else {
              if (!value) return true
              return /^[\da-zA-Z\s-/]{1,32}$/.test(value)  
            } 
          }
        },
        line2: {
          Addressline2 (value) {
            if (!value) return true
            return /^[\da-zA-Z\s-/]{0,15}$/.test(value)
          }
        },
        houseNumber: {
          required (value) {
            if (this.selectedCountry.code === 'ES' || this.selectedCountry.code === 'BE' || this.selectedCountry.code === 'IT' || this.selectedCountry.code === 'AT' || this.selectedCountry.code === 'FR' || this.selectedCountry.code === 'SE' || this.selectedCountry.code === 'CZ' || this.selectedCountry.code === 'RO') {
              return !!value
            } else {
              return true
            }
          },
          houseNumber (value) {
            if (!value) return true
            return this.selectedCountry.houseNumberRegexs.test(value)
          }
        },
        city: {
          required,
          town (value) {
            if(this.selectedCountry.code === 'ES' || this.selectedCountry.code === 'AT' || this.selectedCountry.code === 'SE' || this.selectedCountry.code === 'CZ' || this.selectedCountry.code === 'RO' || this.selectedCountry.code === 'BE'){
              if (!value) return true
              return /^[\da-zA-Z\a-zA-ZùÙüÜáčďéěíňóřšťúůýžäàåáčďéěíňóřšťúůýžâáëèéïìíöòóôüùúÄÀÅÁČĎÉĚÍŇÓŘŠŤÚŮÝŽÁËÈÉÏÌÍÖÒÓÜÚñÑ\s]{1,20}$/.test(value) 
            } else {
              if (!value) return true
              return /^[a-zA-Z\s-]{1,20}$/.test(value) 
            }             
          }
        }, 
        zipCode: {
          required,
          zipCode (value) {
            if (!value) return true
            return this.selectedCountry.zipCodeRegex.test(value)
          }
        }
      }
      //-------State Mapping Validation for country to statecode--------//
      if(!this.selectedCountry.code === 'AT' || !this.selectedCountry.code === 'ES' || !this.selectedCountry.code === 'BE' || !this.selectedCountry.code === 'IT' || !this.selectedCountry.code === 'FR' || !this.selectedCountry.code === 'SE' || !this.selectedCountry.code === 'RO' || !this.selectedCountry.code === 'CZ'){
        shippingValidationObject.county = {
          required,
          town (value) {            
             if (!value) return true
            return /^[a-zA-Z\s-]{1,20}$/.test(value)
          },          
          stateCode (value) { 
            if(this.selectedCountry.code === 'ES'){
            if (!value) return true
            return /^[\da-zA-Z\a-zA-ZùÙüÜáčďéěíňóřšťúůýžäàåáčďéěíňóřšťúůýžáâëèéïìíöòóüùúÄÀÅÁČĎÉĚÍŇÓŘŠŤÚŮÝŽÁËÈÉÏÌÍÖÒÓÜÚñÑ\s]{1,32}$/.test(value) 
          } else {
            if (!value) return true
            return /^[a-zA-Z\s-]{1,20}$/.test(value)
          }             
          }
        } 
      } 
      if (this.selectedCountry.code === 'ES') {
        shippingValidationObject.stateCode = {
          required,
          town(value) {
            if(this.selectedCountry.code === 'ES'){
              if (!value) return true
              return /^[\da-zA-Z\a-zA-ZùÙüÜáčďéěíňóřšťúůýžäàáčďéěíňóřšťúůýžåâáëèéïìíöòóüùúÄÁČĎÉĚÍŇÓŘŠŤÚŮÝŽÀÅÁËÈÉÏÌÍÖÒÓÜÚñÑ\s]{1,32}$/.test(value) 
            } else {
              if (!value) return true
              return /^[a-zA-Z\s-]{1,20}$/.test(value) 
            } 
          }
        } 
      }
      //-------State Mapping Validation end--------//
      billingValidationObject = {
        billingLine1: {
          required,
          Addressline1 (value) { 
            if(this.selectedCountry.code === 'ES' || this.selectedCountry.code === 'AT' || this.selectedCountry.code === 'SE' || this.selectedCountry.code === 'RO' || this.selectedCountry.code === 'CZ' || this.selectedCountry.code === 'BE' || this.selectedCountry.code === 'RO'){
              if (!value) return true
              return /^[\da-zA-Z\a-zA-ZùÙüÜáčďéěíňóřšťúůýžäåàáčďéěíňóřšťúůýžáâëèéïìíöòóüùúÄÀÁČĎÉĚÍŇÓŘŠŤÚŮÝŽÅÁËÈÉÏÌÍÖÒÓÜÚñÑ,\s]{1,32}$/.test(value) 
            } else {
              if (!value) return true
              return /^[\da-zA-Z\s-/]{1,32}$/.test(value)  
            } 
          }
        },
        billingLine2: {
          Addressline2 (value) {
            if (!value) return true
            return /^[\da-zA-Z\s-/]{0,15}$/.test(value)
          }
        },
        billingHouseNumber: {
          required (value) {
            if (this.selectedCountry.code === 'ES' || this.selectedCountry.code === 'BE' || this.selectedCountry.code === 'IT' || this.selectedCountry.code === 'AT' || this.selectedCountry.code === 'FR' || this.selectedCountry.code === 'SE' || this.selectedCountry.code === 'RO' || this.selectedCountry.code === 'CZ') {
              return !!value
            } else {
              return true
            }
          },
          billingHouseNumber (value) {
            if (!value) return true
            return this.selectedCountry.houseNumberRegexs.test(value)
          },
          houseNumber (value) {
            if (!value) return true
            return this.selectedCountry.houseNumberRegexs.test(value)
          }
        },
        billingCity: {
          required,
          town (value) {
            if(this.selectedCountry.code === 'ES' || this.selectedCountry.code === 'AT' || this.selectedCountry.code === 'SE' || this.selectedCountry.code === 'RO' || this.selectedCountry.code === 'CZ' || this.selectedCountry.code === 'BE'){
              if (!value) return true
              return /^[\da-zA-Z\a-zA-ZùÙüÜáčďéěíňóřšťúůýžäâáčďéěíňóřšťúůýžàåáëèéïìíöòóüùúÄÅÁČĎÉĚÍŇÓŘŠŤÚŮÝŽÀÁËÈÉÏÌÍÖÒÓÜÚñÑ\s]{1,20}$/.test(value) 
            } else {
              if (!value) return true
              return /^[a-zA-Z\s-]{1,20}$/.test(value) 
            } 
          }
        },  
        billingZipCode: {
          required,
          zipCode (value) {
            if (!value) return true
            return this.selectedCountry.zipCodeRegex.test(value)
          }
        }
      }
       /*---------state mapping billing object-------------*/  
       if(this.selectedCountry.code === 'GB'){
        billingValidationObject.billingCounty = {
          required,
         town (value) {
           if (!value) return true
           return /^[a-zA-Z\s-]{1,20}$/.test(value)
         }
        } 
      }
      if(this.selectedCountry.code === 'RO'){
        billingValidationObject.billingStateCode = {
          required,
          town (value) {
           if (!value) return true
           return /^[a-zA-Z\s-]{1,20}$/.test(value)
         }
        } 
      }
      if(this.selectedCountry.code === 'IT'){
        billingValidationObject.billingStateCode = {
          required,
          town (value) {
           if (!value) return true
           return /^[a-zA-Z\s-]{1,20}$/.test(value)
         }
        } 
      }
      if (this.selectedCountry.code === 'ES' ) {
        billingValidationObject.billingStateCode ={
          required,
          town (value) {
            if(this.selectedCountry.code === 'ES'){
              if (!value) return true
              return /^[\da-zA-Z\a-zA-ZùÙüÜáčďéěíňóřšťúůýžäàáčďéěíňóřšťúůýžåáëèéïìíöòóüùúÄÀÁČĎÉĚÍŇÓŘŠŤÚŮÝŽÅÁËÈÉÏÌÍÖÒÓÜÚñÑ\s]{1,35}$/.test(value) 
            } else {
              if (!value) return true
              return /^[a-zA-Z\s-]{1,20}$/.test(value) 
            } 
          }   
         }  
       }
       /*--------state mapping billing object end----------*/
    } else {
      shippingValidationObject = {
        street: {
          required (value) {
            if (this.selectedCountry.street_housenumber_extension_field === 3) {
              return !!value
            } else {
              return true
            }
          },
          address (value) {
            if (this.selectedCountry.street_housenumber_extension_field === 3) {
              return !addressRegex.test(value)
            } else {
              return true
            }
          },
          maxLength: maxLength(22),
          minLength: minLength(2)
        },
        addition: {
          required (value) {
            if (this.selectedCountry.street_housenumber_extension_field === 1) {
              return !!value
            } else {
              return true
            }
          },
          includeNumber (value) {
            if (this.selectedCountry.street_housenumber_extension_field === 1) {
              return includeNumberRegex.test(value)
            } else {
              return true
            }
          },
          address (value) {
            if (this.selectedCountry.street_housenumber_extension_field === 1) {
              return !addressRegex.test(value)
            } else {
              return true
            }
          },
          maxLength: maxLength(35),
          minLength: minLength(4)
        },
        extension: {
          maxLength: maxLength(9)
        },
        selectedCountry: {
          required
        },
        selectedCountryState: {
          required (value) {
            if (this.selectedCountry.states) {
              return !!value
            } else {
              return true
            }
          }
        },
        zipCode: {
          required,
          zipCode (value) {
            if (!value) return true
            return this.selectedCountry.zipCodeRegex.test(value)
          }
        },
        city: {
          required,
          town (value) {
            if (!value) return true
              return /^[a-zA-Z\s-]{1,20}$/.test(value) 
          }
        },
        houseNumber: {
          required (value) {
            if (this.selectedCountry.street_housenumber_extension_field === 3) {
              return !!value
            } else {
              return true
            }
          },
          houseNumber (value) {
            if (this.selectedCountry.street_housenumber_extension_field === 3) {
              return this.selectedCountry.houseNumberRegexs.test(value)
            } else {
              return true
            }
          }
        }
      }
      billingValidationObject = {
        billingZipCode: {
          required,
          zipCode (value) {
            if (!value) return true
            return this.selectedBillingCountry.zipCodeRegex.test(value)
          }
        },
        billingCity: {
          required,
          town (value) {
            if (!value) return true
              return /^[a-zA-Z\s-]{1,20}$/.test(value) 
          }
        },
        billingStreet: {
          required (value) {
            if (this.selectedBillingCountry.street_housenumber_extension_field === 3) {
              return !!value
            } else {
              return true
            }
          },
          address (value) {
            if (this.selectedBillingCountry.street_housenumber_extension_field === 3) {
              return !addressRegex.test(value)
            } else {
              return true
            }
          },
          maxLength: maxLength(22),
          minLength: minLength(2)
        },
        billingHouseNumber: {
          required (value) {
            if (this.selectedBillingCountry.street_housenumber_extension_field === 3) {
              return !!value
            } else {
              return true
            }
          },
          houseNumber (value) {
            if (this.selectedBillingCountry.street_housenumber_extension_field === 3) {
              return this.selectedCountry.houseNumberRegexs.test(value)
            } else {
              return true
            }
          }
        },
        billingAddition: {
          required (value) {
            if (this.selectedBillingCountry.street_housenumber_extension_field === 1) {
              return !!value
            } else {
              return true
            }
          },
          includeNumber (value) {
            if (this.selectedBillingCountry.street_housenumber_extension_field === 1) {
              return includeNumberRegex.test(value)
            } else {
              return true
            }
          },
          address (value) {
            if (this.selectedBillingCountry.street_housenumber_extension_field === 1) {
              return !addressRegex.test(value)
            } else {
              return true
            }
          },
          maxLength: maxLength(35),
          minLength: minLength(4)
        },
        billingExtension: {
          maxLength: maxLength(9)
        },
        selectedBillingCountry: {
          required
        },
        selectedBillingCountryState: {
          required (value) {
            if (this.selectedBillingCountry.states) {
              return !!value
            } else {
              return true
            }
          }
        }
      }
    }  
    if (!this.hasBillingAddress) {
      return {
        ...shippingValidationObject
      }
    } else {
      return {
        ...shippingValidationObject,
        ...billingValidationObject
      }
    }
  },

  methods: {
    errorsFor (field) {
      if (!this.showValidations) {
        return []
      }

      if (this.$v[field] == null) {
        return []
      }

      return getErrors(this.$v[field])
    },

    searchState (query, conuntryCode) {
      this.$store.commit('culture/filterStates', {
        query,
        conuntryCode
      })
    },

    searchProvince (query) {
      this.$store.commit('culture/filterProvinces', {
        query
      })
    },

    searchCity (query) {
      this.$store.commit('culture/filterCities', {
        query
      })
    },

    searchBillingCity (query) {
      this.$store.commit('culture/filterBillingCities', {
        query
      })
    },

    editAddress () {
      this.trackConversion({
        name: 'interaction',
        description: `shipping_address:edit:${this.editable ? 'cancel' : 'edit'}`
      })
      if (!this.editable) {
        this.editable = true
        this.showValidations = true
        this.preEditAddress = cloneDeep(this.address)
        if (this.address.shipping.stateCode && this.selectedCountry.code === 'CA' && this.countryProvinces.provinces) {
          this.$store.commit('culture/setCities', this.address.shipping.stateCode)

          if (this.hasBillingAddress) {
            this.$store.commit('culture/setBillingCities', this.address.billing.stateCode)
          }
        }

        this.isShow = true
        this.topLabel = true
      } else {
        this.$store.commit('account/setProfileAddress', this.preEditAddress)
        this.editable = false
        this.isShow = false
      }
    },

    searchCounty (query) {
      this.counties = this.countiesDuplicate.filter(county => startsWith(toLower(county), toLower(query)))
    },

    callbackOnUpdate(data, postCode, stateName){ 
      this.postCode = postCode 
      this.stateName = stateName
      if(!data.valid){ 
          this.$bus.$emit('open-modal', 'launchmodal')           
          this.$notify({
            type: 'error',
            title: this.$t('popuptips.address.update.zipcodenotmatch', ' Please enter a zip code that match with the state')
          })

        if (this.$v.$invalid) {
          this.showValidations = true
          setTimeout(() => {
            this.$scrollTo('[data-scroll="address"] .error', {
              offset: -130
            })
          }, 10)
          if (/checkout/i.test(this.routeName)) {
            this.trackErrorFormFields()
          } 
        }  

       } else {
          this.trackConversion({
          name: 'interaction',
          description: 'shipping_address:next'
        })
           this.$store.commit('account/setProfileAddress', this.address)
          let updateAddressStatus
          let self = this;
          Vue.api.account.updateAddress(this.normalizedAddress,this.allianzCodeApplied).then(function (response) { 
          updateAddressStatus = response.status 
           if(updateAddressStatus == 200){
              self.$notify({
              type: 'success',
              title: self.$t('popuptips.address.update.successful', 'Address updated successfully')
            }) 
            } }).catch(function (error) {
              updateAddressStatus = error.status 
               self.$notify({
                type: 'error',
                title: self.$t('error.message.billingAddressExists', 'This billing address already exists. Use another address or contact the administrator ')
              })
           
         })  
           this.$bus.$emit('update-zipcode', this.normalizedAddress[0].zipCode)
        if (/checkout/i.test(this.routeName)) {
          this.additionalActions()
        }
          this.editable = false 
          this.isShow = false
      }   
     },

     continueaddress(){
          this.$bus.$emit('close-modal', 'launchmodal') 
          this.trackConversion({
          name: 'interaction',
          description: 'shipping_address:next'
        })
          this.$store.commit('account/setProfileAddress', this.address)
          let updateAddressStatus
          let self = this;
          Vue.api.account.updateAddress(this.normalizedAddress,this.allianzCodeApplied).then(function (response) { 
          updateAddressStatus = response.status 
           if(updateAddressStatus == 200){
              self.$notify({
              type: 'success',
              title: self.$t('popuptips.address.update.successful', 'Address updated successfully')
            }) 
            } }).catch(function (error) {
              updateAddressStatus = error.status 
               self.$notify({
                type: 'error',
                title: self.$t('error.message.billingAddressExists', 'This billing address already exists. Use another address or contact the administrator ')
              })
           
         })  
          this.$bus.$emit('update-zipcode', this.normalizedAddress[0].zipCode)
        if (/checkout/i.test(this.routeName)) {
          this.additionalActions()
        }
        this.editable = false  
        this.isShow = false
      },

      modify(){
        this.$bus.$emit('close-modal', 'launchmodal')
      }

  },

  tasks (t) {
    return {
      updateAddressTask: t(async function () {
        if(this.cartDetails.promotions && this.cartDetails.promotions !== 'undefined'){
          if( this.cartDetails.promotions.length !== 0  &&  this.cartDetails.firstTermTotalAmount == 0){
            this.allianzCodeApplied = true
           } else {
           this.allianzCodeApplied = false
          }
        }
        if(this.currentCountryCode === 'ES' && this.line2 == ''){
          this.shipping.line2 = null;
          this.billing.line2 = null;
        }
        if((this.currentCountryCode === 'US') && (!this.$v.$invalid)){
          Vue.api.account.updateAddressTask(this.zipCode,this.selectedCountryState,this.callbackOnUpdate)   
        } else {
        this.$v.$touch()
        if (this.$v.$invalid) {
          this.showValidations = true
          setTimeout(() => {
            this.$scrollTo('[data-scroll="address"] .error', {
              offset: -130
            })
          }, 10)
          if (/checkout/i.test(this.routeName)) {
            this.trackErrorFormFields()
          }
        } else {
          this.trackConversion({
            name: 'interaction',
            description: 'shipping_address:next'
          })
          await this.$store.commit('account/setProfileAddress', this.address)
          await Vue.api.account.updateAddress(this.normalizedAddress,this.allianzCodeApplied)
          this.$bus.$emit('update-zipcode', this.normalizedAddress[0].zipCode)
          if (/checkout/i.test(this.routeName)) {
            this.additionalActions()
          }
          this.editable = false

          this.$notify({
            type: 'success',
            title: this.$t('popuptips.address.update.successful', 'Address updated successfully')
          })
          this.isShow = false
        }
      }
      }).onError(function (task) {
        this.$notify({
          type: 'error',
          title: extractErrorMessage(task.error)
        })
        this.$scrollTo('[data-scroll="address"]', {
          offset: -60
        })
      }).flow('drop')
    }
  },

  mounted () {
    const editableButton = document.getElementsByClassName('AppEditButton')
    if (editableButton.length > 1 && this.$v.$invalid) {
      this.editable = true
      this.showValidations = true
      this.show = true
    }
  }
}
